// This route file is for the B2B admin routes userType = 3
import { lazy } from 'react';
import CBadges from '../container/B2B/CompanyAdmin/Badges/CBadges';
import ThreePlaneDashboard from '../container/B2B/CompanyAdmin/Dashboard/ThreePlaneDashboard';
import CompanyBreathworkLayout from '../container/B2B/CompanyAdmin/Breathwork/CompanyBreathworkLayout';
import CBreathworkSessions from '../container/B2B/CompanyAdmin/Breathwork/CBreathworkSessions';
import CBreathworkAudio from '../container/B2B/CompanyAdmin/Breathwork/CBreathworkAudio';
import CBreathworkUsers from '../container/B2B/CompanyAdmin/Breathwork/CBreathworkUsers';
import CBreathworkUserDetail from '../container/B2B/CompanyAdmin/Breathwork/CBreathworkUserDetail';

// Company Shoorah Pods section — listening analytics over the pods catalogue.
// Reached from the Shoorah Pods card on the dashboard; the catalogue itself
// stays on the Shoorah-admin side at /shoorah-pods/manage.
import CompanyPodsLayout from '../container/B2B/CompanyAdmin/ShoorahPods/CompanyPodsLayout';
import CPodsDashboard from '../container/B2B/CompanyAdmin/ShoorahPods/CPodsDashboard';
import CPodsUsers from '../container/B2B/CompanyAdmin/ShoorahPods/CPodsUsers';
import CPodsUserDetail from '../container/B2B/CompanyAdmin/ShoorahPods/CPodsUserDetail';

// Company Meditation Sounds section — practice analytics over the meditation
// catalogue. Reached from the Meditation card on the dashboard; the catalogue
// itself stays on the Shoorah-admin side at /meditation/manages.
import CompanyMeditationLayout from '../container/B2B/CompanyAdmin/Meditation/CompanyMeditationLayout';
import CMeditationDashboard from '../container/B2B/CompanyAdmin/Meditation/CMeditationDashboard';
import CMeditationUsers from '../container/B2B/CompanyAdmin/Meditation/CMeditationUsers';
import CMeditationUserDetail from '../container/B2B/CompanyAdmin/Meditation/CMeditationUserDetail';

// Company Shoorah Videos section — viewing analytics across BOTH video
// libraries at once: Health/NHS videos (Vimeo embeds) and Shoorah Vids
// (uploaded in the admin panel). Reached from the Shoorah Vids card on the
// dashboard. Distinct from /:slug/health-videos, which is a content browser
// rather than analytics.
import CompanyVidsLayout from '../container/B2B/CompanyAdmin/Vids/CompanyVidsLayout';
import CVidsDashboard from '../container/B2B/CompanyAdmin/Vids/CVidsDashboard';
import CVidsUsers from '../container/B2B/CompanyAdmin/Vids/CVidsUsers';
import CVidsUserDetail from '../container/B2B/CompanyAdmin/Vids/CVidsUserDetail';

// Company Shuru section — the AI companion's conversation analytics. Reported
// in aggregate: reach, volume, how people felt afterwards and the workplace
// themes their messages matched. Message content is never exposed, at company
// level or per user.
import CompanyShuruLayout from '../container/B2B/CompanyAdmin/Shuru/CompanyShuruLayout';
import CShuruDashboard from '../container/B2B/CompanyAdmin/Shuru/CShuruDashboard';
import CShuruUsers from '../container/B2B/CompanyAdmin/Shuru/CShuruUsers';
import CShuruUserDetail from '../container/B2B/CompanyAdmin/Shuru/CShuruUserDetail';

// Company Mood Tracking section — the Mood Log check-ins (emotion + context),
// not the legacy slider moods on the main dashboard. Notes attached to a
// check-in are encrypted health data and are never surfaced here.
import CompanyMoodLayout from '../container/B2B/CompanyAdmin/Mood/CompanyMoodLayout';
import CMoodDashboard from '../container/B2B/CompanyAdmin/Mood/CMoodDashboard';
import CMoodUsers from '../container/B2B/CompanyAdmin/Mood/CMoodUsers';
import CMoodUserDetail from '../container/B2B/CompanyAdmin/Mood/CMoodUserDetail';

// Company Self-Assessment section — the weighted questionnaires and their runs.
// The details a person fills in before starting (dob, ethnicity, job role,
// medication and GP answers) are special-category data and never surfaced here.
import CompanySelfAssessmentLayout from '../container/B2B/CompanyAdmin/SelfAssessment/CompanySelfAssessmentLayout';
import CSelfAssessmentDashboard from '../container/B2B/CompanyAdmin/SelfAssessment/CSelfAssessmentDashboard';
import CSelfAssessmentUsers from '../container/B2B/CompanyAdmin/SelfAssessment/CSelfAssessmentUsers';
import CSelfAssessmentUserDetail from '../container/B2B/CompanyAdmin/SelfAssessment/CSelfAssessmentUserDetail';

// Company Home Testing section — the Vitall partner's kit orders. Volume, stage
// and timing only; biomarker results, report files and the personal details the
// partner holds are never surfaced here.
import CompanyHomeTestingLayout from '../container/B2B/CompanyAdmin/HomeTesting/CompanyHomeTestingLayout';
import CHomeTestingDashboard from '../container/B2B/CompanyAdmin/HomeTesting/CHomeTestingDashboard';
import CHomeTestingUsers from '../container/B2B/CompanyAdmin/HomeTesting/CHomeTestingUsers';
import CHomeTestingUserDetail from '../container/B2B/CompanyAdmin/HomeTesting/CHomeTestingUserDetail';

// Company Vision Board section — take-up and whether boards get filled in.
// Board titles, the words and photos on them, the stories and the tags are
// never surfaced here.
import CompanySelfDevelopmentLayout from '../container/B2B/CompanyAdmin/SelfDevelopment/CompanySelfDevelopmentLayout';
import CSelfDevelopmentOverview from '../container/B2B/CompanyAdmin/SelfDevelopment/CSelfDevelopmentOverview';
import CSelfDevelopmentTool from '../container/B2B/CompanyAdmin/SelfDevelopment/CSelfDevelopmentTool';
import CSelfDevVisionBoard from '../container/B2B/CompanyAdmin/SelfDevelopment/CSelfDevVisionBoard';
import CSelfDevelopmentUsers from '../container/B2B/CompanyAdmin/SelfDevelopment/CSelfDevelopmentUsers';
import CSelfDevelopmentUserDetail from '../container/B2B/CompanyAdmin/SelfDevelopment/CSelfDevelopmentUserDetail';
import {
  VisionBoardDashboardRedirect,
  VisionBoardUsersRedirect,
  VisionBoardUserRedirect
} from '../container/B2B/CompanyAdmin/SelfDevelopment/VisionBoardRedirect';
import CompanyScansLayout from '../container/B2B/CompanyAdmin/Scans/CompanyScansLayout';
import CScansOverview from '../container/B2B/CompanyAdmin/Scans/CScansOverview';
import CScanPage from '../container/B2B/CompanyAdmin/Scans/CScanPage';
import CScanUsers from '../container/B2B/CompanyAdmin/Scans/CScanUsers';
import CScanUserDetail from '../container/B2B/CompanyAdmin/Scans/CScanUserDetail';
import CompanyCessationLayout from '../container/B2B/CompanyAdmin/Cessation/CompanyCessationLayout';
import CCessationOverview from '../container/B2B/CompanyAdmin/Cessation/CCessationOverview';
import CProgrammePage from '../container/B2B/CompanyAdmin/Cessation/CProgrammePage';
import CompanyTriggerLayout from '../container/B2B/CompanyAdmin/TriggerTrace/CompanyTriggerLayout';
import CTriggerOverview from '../container/B2B/CompanyAdmin/TriggerTrace/CTriggerOverview';
import CTriggerPatterns from '../container/B2B/CompanyAdmin/TriggerTrace/CTriggerPatterns';
import CTriggerResolution from '../container/B2B/CompanyAdmin/TriggerTrace/CTriggerResolution';
import CTriggerUsers from '../container/B2B/CompanyAdmin/TriggerTrace/CTriggerUsers';
import CompanyGamesLayout from '../container/B2B/CompanyAdmin/Games/CompanyGamesLayout';
import CGamesOverview from '../container/B2B/CompanyAdmin/Games/CGamesOverview';
import CChess from '../container/B2B/CompanyAdmin/Games/CChess';
import CWordSearch from '../container/B2B/CompanyAdmin/Games/CWordSearch';
import CBlockFill from '../container/B2B/CompanyAdmin/Games/CBlockFill';
import CQuiz from '../container/B2B/CompanyAdmin/Games/CQuiz';
import CGamesUsers from '../container/B2B/CompanyAdmin/Games/CGamesUsers';
import CompanyGuidedPathLayout from '../container/B2B/CompanyAdmin/GuidedPath/CompanyGuidedPathLayout';
import CGuidedPathOverview from '../container/B2B/CompanyAdmin/GuidedPath/CGuidedPathOverview';
import {
  CGuidedPathAiPlan,
  CGuidedPathCustomPlan
} from '../container/B2B/CompanyAdmin/GuidedPath/CPlanTypePage';
import CGuidedPathUsers from '../container/B2B/CompanyAdmin/GuidedPath/CGuidedPathUsers';
import CompanyCareLayout from '../container/B2B/CompanyAdmin/Care/CompanyCareLayout';
import CompanyReproductiveLayout from '../container/B2B/CompanyAdmin/Care/CompanyReproductiveLayout';
import {
  CCareOverview,
  CReproductiveOverview
} from '../container/B2B/CompanyAdmin/Care/CSectionOverview';
import {
  CCareProgramme,
  CReproductiveProgramme
} from '../container/B2B/CompanyAdmin/Care/CProgrammePage';
import CVisionBoardUsers from '../container/B2B/CompanyAdmin/VisionBoard/CVisionBoardUsers';
import CVisionBoardUserDetail from '../container/B2B/CompanyAdmin/VisionBoard/CVisionBoardUserDetail';

// Company MoneyMind section — take-up and the keep / unsure / let-go mix. No
// amount, no currency total, no income and no savings appear anywhere in it.
import CompanyMoneyMindLayout from '../container/B2B/CompanyAdmin/MoneyMind/CompanyMoneyMindLayout';
import CMoneyMindDashboard from '../container/B2B/CompanyAdmin/MoneyMind/CMoneyMindDashboard';
import CMoneyMindUsers from '../container/B2B/CompanyAdmin/MoneyMind/CMoneyMindUsers';
import CMoneyMindUserDetail from '../container/B2B/CompanyAdmin/MoneyMind/CMoneyMindUserDetail';

// Company Elite Mind section — the athlete squad. Reports count only where the
// athlete set share_with_team; the daily journal is counted but never read.
import CompanyEliteMindLayout from '../container/B2B/CompanyAdmin/EliteMind/CompanyEliteMindLayout';
import CEliteMindDashboard from '../container/B2B/CompanyAdmin/EliteMind/CEliteMindDashboard';
import CEliteMindUsers from '../container/B2B/CompanyAdmin/EliteMind/CEliteMindUsers';
import CEliteMindUserDetail from '../container/B2B/CompanyAdmin/EliteMind/CEliteMindUserDetail';

// Company Sleep section (was registered only in RouteFile.js, so B2B admins —
// who load this file — could not reach it).
import CompanySleepLayout from '../container/B2B/CompanyAdmin/Sleep/CompanySleepLayout';
import CSleepTracker from '../container/B2B/CompanyAdmin/Sleep/CSleepTracker';
import CSleepSounds from '../container/B2B/CompanyAdmin/Sleep/CSleepSounds';
import CSleepUsers from '../container/B2B/CompanyAdmin/Sleep/CSleepUsers';
import CSleepUserDetail from '../container/B2B/CompanyAdmin/Sleep/CSleepUserDetail';

// Company Habit Tracker section
import CompanyHabitsLayout from '../container/B2B/CompanyAdmin/Habits/CompanyHabitsLayout';
import CHabitsTracker from '../container/B2B/CompanyAdmin/Habits/CHabitsTracker';
import CHabitsUsers from '../container/B2B/CompanyAdmin/Habits/CHabitsUsers';
import CHabitsUserDetail from '../container/B2B/CompanyAdmin/Habits/CHabitsUserDetail';

// Company Challenges insights (analytics). Creating and editing challenges
// keeps /:slug/challenges; these live under /:slug/challenges/insights, the
// same split Marketplace uses.
import CompanyChallengesLayout from '../container/B2B/CompanyAdmin/Challenges/CompanyChallengesLayout';
import CChallengesDashboard from '../container/B2B/CompanyAdmin/Challenges/CChallengesDashboard';
import CChallengesList from '../container/B2B/CompanyAdmin/Challenges/CChallengesList';
import CChallengesUsers from '../container/B2B/CompanyAdmin/Challenges/CChallengesUsers';
import CChallengesUserDetail from '../container/B2B/CompanyAdmin/Challenges/CChallengesUserDetail';

// Company Marketplace insights (analytics). The shopping experience keeps
// /:slug/marketplace; these live under /:slug/marketplace/insights.
import CompanyMarketplaceLayout from '../container/B2B/CompanyAdmin/MarketplaceInsights/CompanyMarketplaceLayout';
import CMarketplaceEGifts from '../container/B2B/CompanyAdmin/MarketplaceInsights/CMarketplaceEGifts';
import CMarketplaceVip from '../container/B2B/CompanyAdmin/MarketplaceInsights/CMarketplaceVip';
import CMarketplaceUsers from '../container/B2B/CompanyAdmin/MarketplaceInsights/CMarketplaceUsers';
import CMarketplaceUserDetail from '../container/B2B/CompanyAdmin/MarketplaceInsights/CMarketplaceUserDetail';

// Company Perks — the umbrella over Instant Support (EAP), Salary Sacrifice,
// Health Scheme and Custom Perks. Analytics only; the management pages for
// each programme keep their own routes.
import CompanyPerksLayout from '../container/B2B/CompanyAdmin/Perks/CompanyPerksLayout';
import CPerksOverview from '../container/B2B/CompanyAdmin/Perks/CPerksOverview';
import CPerksInstantSupport from '../container/B2B/CompanyAdmin/Perks/CPerksInstantSupport';
import CPerksSalarySacrifice from '../container/B2B/CompanyAdmin/Perks/CPerksSalarySacrifice';
import CPerksHealthScheme from '../container/B2B/CompanyAdmin/Perks/CPerksHealthScheme';
import CPerksCustom from '../container/B2B/CompanyAdmin/Perks/CPerksCustom';
import CPerksUsers from '../container/B2B/CompanyAdmin/Perks/CPerksUsers';
import CPerksUserDetail from '../container/B2B/CompanyAdmin/Perks/CPerksUserDetail';
import WellbankInvoiceDetails from '../container/Peap/WellBank/PurchaseHistory/InvoiceDetails';
import BTBAddNotification from '../container/PushNotification/BTBAddNotification';
import ViewNotification from '../container/ViewNotification';
import Integrations from '../container/B2B/CompanyAdmin/Integrations/Integrations';
import NotFound from '../container/404NotFound';
import InstantSupportDashboard from '../container/InstantSupport';
import ViewBenefitsPage from '../container/Benefits/ViewBenefits';
import AddCustomBenefitsPage from '../container/Benefits/AddCustomBenefits';
import PulseSurveyDashboard from '../container/PulseSurvey';
import AddEditPulseSurvey from '../container/PulseSurvey/addSurvey/addSurvey';
import PreviewPulseSurveys from '../container/PulseSurvey/preview/preivewPulseSurvey';
import PulseSurvey from '../container/PulseSurvey/pulseSurveyList';
import ContentApproval from '../container/ContentApproval/ContentApproval';
import ViewContentApproval from '../container/ContentApproval/ViewContentApproval';
import ViewSurveyApproval from '../container/ContentApproval/ViewSurveyApproval';
import UnderMaintenance from '../container/userMaintenance';

import AddEditRewards from '../container/B2B/CompanyAdmin/TeamChallanges/AddEditRewards';
import AddEditChallanges from '../container/B2B/CompanyAdmin/TeamChallanges/AddEditChallanges';
import ViewAllChallanges from '../container/B2B/CompanyAdmin/TeamChallanges/ViewAllChallanges';
import ViewAllRewards from '../container/B2B/CompanyAdmin/TeamChallanges/ViewAllRewards';
import AddEditWeeklyChallanges from '../container/B2B/CompanyAdmin/TeamChallanges/AddEditWeeklyChallanges';
import WellBankDashboard from '../container/Peap/dashboard/WellBankDashboard';
import GetSupport from '../container/InstantSupport/GetSupport';
import InstantSupportCheckoutPage from '../container/InstantSupport/Checkout';
import UpgradeUsersForInstantSupportPage from '../container/InstantSupport/UpgradePlans';
import InstantSupportAddCustomNumber from '../container/InstantSupport/AddCustomNumber';

import Challenges from '../container/ChallengesAndTasks/challenges/Challenges';
import NewChallenges from '../container/ChallengesAndTasks/challenges/NewChallenges';
import ViewChallenges from '../container/ChallengesAndTasks/challenges/ViewChallenges';
import HealthVideos from '../container/HealthVideos/HealthVideos';
import AllHealthVideos from '../container/HealthVideos/AllHealthVideos';
import ViewHealthVideo from '../container/HealthVideos/ViewHealthVideo';
import eliteGameRoutes from './eliteGameRoute';
import EliteGames from '../container/EliteGames/EliteGames';
import Kids from '../container/KidsContentManagement/Kids/Kids';

// Salary Sacrifice Routes
import SalarySacrifice from '../container/B2B/CompanyAdmin/SalarySacrifice/SalarySacrifice';
import AddEditScheme from '../container/B2B/CompanyAdmin/SalarySacrifice/AddEditScheme';
import EnrollmentsList from '../container/B2B/CompanyAdmin/SalarySacrifice/EnrollmentsList';

// Health Scheme Routes
import HealthSchemePage from '../container/HealthScheme';
import AddEditHealthScheme from '../container/HealthScheme/AddEditHealthScheme';
import AdminHealthSchemes from '../container/HealthScheme/AdminHealthSchemes';
import KidsView from '../container/KidsContentManagement/Kids/KidsView';
import Peptides from '../container/peptide/peptides';
import peptideRoutes from './peptide';
import B2BOfferings from '../container/Offerings/B2BOfferings';
import B2BCreateCustomOffering from '../container/Offerings/B2BCreateCustomOffering';
import ManageOffering from '../container/Offerings/ManageOffering';
import Marketplace from '../container/B2B/Marketplace/Marketplace';
import Cart from '../container/B2B/Marketplace/Cart';
import Orders from '../container/B2B/Marketplace/Orders';
import ProductDetail from '../container/B2B/Marketplace/ProductDetail';

// Shoutouts Routes
import Shoutouts from '../container/B2B/Shoutouts';
import SendShoutout from '../container/B2B/Shoutouts/SendShoutout';

// People Plan Routes
import PeoplePlanDashboard from '../container/B2B/CompanyAdmin/PeoplePlan/Dashboard/PeoplePlanDashboard';
import PeoplePlanGoals from '../container/B2B/CompanyAdmin/PeoplePlan/Goals/Goals';
import AddEditGoal from '../container/B2B/CompanyAdmin/PeoplePlan/Goals/AddEditGoal';
import ViewGoal from '../container/B2B/CompanyAdmin/PeoplePlan/Goals/ViewGoal';
import AddEditMilestone from '../container/B2B/CompanyAdmin/PeoplePlan/Milestones/AddEditMilestone';
import ViewMilestone from '../container/B2B/CompanyAdmin/PeoplePlan/Milestones/ViewMilestone';
import PeoplePlanKeyDates from '../container/B2B/CompanyAdmin/PeoplePlan/KeyDates/KeyDates';
import PeoplePlanDocumentHub from '../container/B2B/CompanyAdmin/PeoplePlan/DocumentHub/DocumentHub';
import PeoplePlanDocumentFolderDetail from '../container/B2B/CompanyAdmin/PeoplePlan/DocumentHub/DocumentFolderDetail';
import PeoplePlanRoadMaps from '../container/B2B/CompanyAdmin/PeoplePlan/RoadMaps/RoadMaps';
import AddEditRoadMap from '../container/B2B/CompanyAdmin/PeoplePlan/RoadMaps/AddEditRoadMap';
import ViewRoadMap from '../container/B2B/CompanyAdmin/PeoplePlan/RoadMaps/ViewRoadMap';
import PeoplePlanRoiReport from '../container/B2B/CompanyAdmin/PeoplePlan/RoiReport/RoiReport';

const withCompanySleepLayout = (Component) => () =>
  (
    <CompanySleepLayout>
      <Component />
    </CompanySleepLayout>
  );

const withCompanyHabitsLayout = (Component) => () =>
  (
    <CompanyHabitsLayout>
      <Component />
    </CompanyHabitsLayout>
  );

const withCompanyChallengesLayout = (Component) => () =>
  (
    <CompanyChallengesLayout>
      <Component />
    </CompanyChallengesLayout>
  );

const withCompanyMarketplaceLayout = (Component) => () =>
  (
    <CompanyMarketplaceLayout>
      <Component />
    </CompanyMarketplaceLayout>
  );

const withCompanyBreathworkLayout = (Component) => () =>
  (
    <CompanyBreathworkLayout>
      <Component />
    </CompanyBreathworkLayout>
  );

const withCompanyPodsLayout = (Component) => () =>
  (
    <CompanyPodsLayout>
      <Component />
    </CompanyPodsLayout>
  );

const withCompanyMeditationLayout = (Component) => () =>
  (
    <CompanyMeditationLayout>
      <Component />
    </CompanyMeditationLayout>
  );

const withCompanyVidsLayout = (Component) => () =>
  (
    <CompanyVidsLayout>
      <Component />
    </CompanyVidsLayout>
  );

const withCompanyShuruLayout = (Component) => () =>
  (
    <CompanyShuruLayout>
      <Component />
    </CompanyShuruLayout>
  );

const withCompanyMoodLayout = (Component) => () =>
  (
    <CompanyMoodLayout>
      <Component />
    </CompanyMoodLayout>
  );

const withCompanySelfAssessmentLayout = (Component) => () =>
  (
    <CompanySelfAssessmentLayout>
      <Component />
    </CompanySelfAssessmentLayout>
  );

const withCompanyHomeTestingLayout = (Component) => () =>
  (
    <CompanyHomeTestingLayout>
      <Component />
    </CompanyHomeTestingLayout>
  );

/**
 * Vision Board is a tab inside Self Development now, so its pages wear the Self
 * Development shell — same tab list, same department filter, same title.
 */
const withCompanySelfDevelopmentLayout = (Component) => () =>
  (
    <CompanySelfDevelopmentLayout>
      <Component />
    </CompanySelfDevelopmentLayout>
  );

const withCompanyScansLayout = (Component) => () =>
  (
    <CompanyScansLayout>
      <Component />
    </CompanyScansLayout>
  );

const withCompanyCessationLayout = (Component) => () =>
  (
    <CompanyCessationLayout>
      <Component />
    </CompanyCessationLayout>
  );

const withCompanyTriggerLayout = (Component) => () =>
  (
    <CompanyTriggerLayout>
      <Component />
    </CompanyTriggerLayout>
  );

const withCompanyGamesLayout = (Component) => () =>
  (
    <CompanyGamesLayout>
      <Component />
    </CompanyGamesLayout>
  );

const withCompanyGuidedPathLayout = (Component) => () =>
  (
    <CompanyGuidedPathLayout>
      <Component />
    </CompanyGuidedPathLayout>
  );

// Two layouts, one folder. Care Programmes and Reproductive Health are separate
// route trees so a customer can be given one and not the other; the pages
// behind them are the same shape because the question they answer is the same.
const withCompanyCareLayout = (Component) => () =>
  (
    <CompanyCareLayout>
      <Component />
    </CompanyCareLayout>
  );

const withCompanyReproductiveLayout = (Component) => () =>
  (
    <CompanyReproductiveLayout>
      <Component />
    </CompanyReproductiveLayout>
  );

const withCompanyMoneyMindLayout = (Component) => () =>
  (
    <CompanyMoneyMindLayout>
      <Component />
    </CompanyMoneyMindLayout>
  );

const withCompanyEliteMindLayout = (Component) => () =>
  (
    <CompanyEliteMindLayout>
      <Component />
    </CompanyEliteMindLayout>
  );

// Perks is the only section whose layout also gates access — it fetches the
// company's programme availability and bounces a company that has none of
// them, so every page below must be wrapped in it, not just decorated by it.
const withCompanyPerksLayout = (Component) => () =>
  (
    <CompanyPerksLayout>
      <Component />
    </CompanyPerksLayout>
  );

const Login = lazy(() => import('../container/Login'));
const ForgotPassword = lazy(() => import('../container/ForgotPassword'));
const ResetPassword = lazy(() => import('../container/ResetPassword'));
const ChangePassword = lazy(() => import('../container/ChangePassword'));
const Notification = lazy(() => import('../container/Notification'));
const SubAdmin = lazy(() => import('../container/SubAdmin/SubAdmin'));
const AddEditSubAdmin = lazy(() => import('../container/SubAdmin/AddEditSubAdmin'));
const ViewEditUser = lazy(() => import('../container/Users/ViewEditUser'));

const MyCompany = lazy(() => import('../container/B2B/CompanyAdmin/MyCompany/MyCompany'));
const ChildCompanies = lazy(() =>
  import('../container/B2B/CompanyAdmin/ChildCompanies/ChildCompanies')
);
const CompanyUser = lazy(() => import('../container/B2B/CompanyAdmin/CUsers/CUser'));
const CompanyReport = lazy(() => import('../container/B2B/CompanyAdmin/Reports/CReports'));
const CompanySolution = lazy(() => import('../container/B2B/CompanyAdmin/Solution/CSolution'));
const AddUser = lazy(() => import('../container/B2B/CompanyAdmin/CUsers/AddUser'));
// Survey Routes Starts
const AddEditSurvey = lazy(() => import('../container/Survey/AddEditSurvey'));
const PreviewSurvey = lazy(() => import('../container/Survey/PreviewSurvey'));
const MySurvey = lazy(() => import('../container/Survey/MySurvey'));
const MyPulseSurvey = lazy(() => import('../container/PulseSurvey/MyPulseSurvey'));
const SurveyDashboard = lazy(() => import('../container/Survey/SurveyDashboard'));
const SurveyStatistics = lazy(() => import('../container/Survey/SurveyStatistics'));
// Survey Routes Ends

// Peap Routes
const ManageWellPoint = lazy(() =>
  import('../container/Peap/WellBank/ManageWellPoint/ManageWellPoint')
);
const ManageRequest = lazy(() => import('../container/Peap/WellBank/ManageRequest/ManageRequest'));
const PurchaseWellPoint = lazy(() =>
  import('../container/Peap/WellBank/PurchaseWellPoint/PurchaseWellPoint')
);
const BuyNowWellPoint = lazy(() => import('../container/Peap/WellBank/PurchaseWellPoint/BuyNow'));
const BuyNowSummary = lazy(() =>
  import('../container/Peap/WellBank/PurchaseWellPoint/BuyNowSummary')
);
const OrderNowWellPoint = lazy(() =>
  import('../container/Peap/WellBank/PurchaseWellPoint/OrderNow')
);
const OrderNowSummary = lazy(() =>
  import('../container/Peap/WellBank/PurchaseWellPoint/OrderNowSummary')
);
const PurchaseHistory = lazy(() =>
  import('../container/Peap/WellBank/PurchaseHistory/PurchaseHistory')
);
const TransactionHistory = lazy(() =>
  import('../container/Peap/WellBank/TransactionHistory/TransactionHistory')
);
const IndividualDistribution = lazy(() =>
  import('../container/Peap/WellBank/IndividualDistribution/IndividualDistribution')
);
const IndividualAmount = lazy(() =>
  import('../container/Peap/WellBank/IndividualDistribution/IndividualAmount')
);
const EqualAmount = lazy(() =>
  import('../container/Peap/WellBank/IndividualDistribution/EqualAmount')
);
const TeamDistribution = lazy(() =>
  import('../container/Peap/WellBank/TeamDistribution/TeamDistribution')
);
const TeamIndividualAmount = lazy(() =>
  import('../container/Peap/WellBank/TeamDistribution/IndividualAmount')
);
const TeamEqualAmount = lazy(() =>
  import('../container/Peap/WellBank/TeamDistribution/EqualAmount')
);
const CompanyDistribution = lazy(() =>
  import('../container/Peap/WellBank/CompanyDistribution/CompanyDistribution')
);
const AutoWithdrawDistribution = lazy(() =>
  import('../container/Peap/WellBank/AutoWithdrawDistribution/AutoWithdrawDistribution')
);

const routes = [
  {
    path: '/login',
    exact: true,
    name: 'Login',
    component: Login,
    private: false
  },
  {
    path: '/',
    exact: true,
    name: 'Login',
    component: Login,
    private: false
  },
  {
    path: '/forgot-password',
    exact: true,
    name: 'ForgotPassword',
    component: ForgotPassword,
    private: false
  },
  {
    path: '/reset-password',
    exact: true,
    name: 'ResetPassword',
    component: ResetPassword,
    private: false
  },
  {
    path: '/change-password',
    exact: true,
    name: 'ChangePassword',
    component: ChangePassword,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/notifications',
    exact: true,
    name: 'Notification',

    component: ViewNotification,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/users/edit',
    exact: true,
    name: 'ViewEditUser',
    component: ViewEditUser,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    // The rebuilt three-plane dashboard is now the company dashboard.
    // NewCDashboard survives only inside the super admin's View Company tab.
    path: '/:slug/dashboard',
    exact: true,
    name: 'CompanyDashboard',
    component: ThreePlaneDashboard,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/breathwork',
    exact: true,
    name: 'CompanyBreathworkSessions',
    component: withCompanyBreathworkLayout(CBreathworkSessions),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/breathwork/audio',
    exact: true,
    name: 'CompanyBreathworkAudio',
    component: withCompanyBreathworkLayout(CBreathworkAudio),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/breathwork/users',
    exact: true,
    name: 'CompanyBreathworkUsers',
    component: withCompanyBreathworkLayout(CBreathworkUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/breathwork/user',
    exact: true,
    name: 'CompanyBreathworkUserDetail',
    component: withCompanyBreathworkLayout(CBreathworkUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-pods',
    exact: true,
    name: 'CompanyPodsDashboard',
    component: withCompanyPodsLayout(CPodsDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-pods/users',
    exact: true,
    name: 'CompanyPodsUsers',
    component: withCompanyPodsLayout(CPodsUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-pods/user',
    exact: true,
    name: 'CompanyPodsUserDetail',
    component: withCompanyPodsLayout(CPodsUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/meditation',
    exact: true,
    name: 'CompanyMeditationDashboard',
    component: withCompanyMeditationLayout(CMeditationDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/meditation/users',
    exact: true,
    name: 'CompanyMeditationUsers',
    component: withCompanyMeditationLayout(CMeditationUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/meditation/user',
    exact: true,
    name: 'CompanyMeditationUserDetail',
    component: withCompanyMeditationLayout(CMeditationUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-videos',
    exact: true,
    name: 'CompanyVidsDashboard',
    component: withCompanyVidsLayout(CVidsDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-videos/users',
    exact: true,
    name: 'CompanyVidsUsers',
    component: withCompanyVidsLayout(CVidsUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoorah-videos/user',
    exact: true,
    name: 'CompanyVidsUserDetail',
    component: withCompanyVidsLayout(CVidsUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shuru',
    exact: true,
    name: 'CompanyShuruDashboard',
    component: withCompanyShuruLayout(CShuruDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shuru/users',
    exact: true,
    name: 'CompanyShuruUsers',
    component: withCompanyShuruLayout(CShuruUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shuru/user',
    exact: true,
    name: 'CompanyShuruUserDetail',
    component: withCompanyShuruLayout(CShuruUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/mood',
    exact: true,
    name: 'CompanyMoodDashboard',
    component: withCompanyMoodLayout(CMoodDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/mood/users',
    exact: true,
    name: 'CompanyMoodUsers',
    component: withCompanyMoodLayout(CMoodUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/mood/user',
    exact: true,
    name: 'CompanyMoodUserDetail',
    component: withCompanyMoodLayout(CMoodUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-assessment',
    exact: true,
    name: 'CompanySelfAssessmentDashboard',
    component: withCompanySelfAssessmentLayout(CSelfAssessmentDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-assessment/users',
    exact: true,
    name: 'CompanySelfAssessmentUsers',
    component: withCompanySelfAssessmentLayout(CSelfAssessmentUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-assessment/user',
    exact: true,
    name: 'CompanySelfAssessmentUserDetail',
    component: withCompanySelfAssessmentLayout(CSelfAssessmentUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/home-testing',
    exact: true,
    name: 'CompanyHomeTestingDashboard',
    component: withCompanyHomeTestingLayout(CHomeTestingDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/home-testing/users',
    exact: true,
    name: 'CompanyHomeTestingUsers',
    component: withCompanyHomeTestingLayout(CHomeTestingUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/home-testing/user',
    exact: true,
    name: 'CompanyHomeTestingUserDetail',
    component: withCompanyHomeTestingLayout(CHomeTestingUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // ---- Self Development: Gratitude, Cleanse, Journal, Goals, Vision Board,
  // Affirmations. Six tools reported as one practice.
  {
    path: '/:slug/self-development',
    exact: true,
    name: 'CompanySelfDevelopmentOverview',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // One component serves all five tool pages — it reads the tool from the last
  // path segment, which is also the key the backend expects.
  {
    path: '/:slug/self-development/gratitude',
    exact: true,
    name: 'CompanySelfDevelopmentGratitude',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentTool),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/cleanse',
    exact: true,
    name: 'CompanySelfDevelopmentCleanse',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentTool),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/journal',
    exact: true,
    name: 'CompanySelfDevelopmentJournal',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentTool),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/goals',
    exact: true,
    name: 'CompanySelfDevelopmentGoals',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentTool),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/affirmations',
    exact: true,
    name: 'CompanySelfDevelopmentAffirmations',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentTool),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Vision Board's own three pages, re-parented under Self Development.
  {
    path: '/:slug/self-development/vision-board',
    exact: true,
    name: 'CompanyVisionBoardDashboard',
    component: withCompanySelfDevelopmentLayout(CSelfDevVisionBoard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/vision-board/users',
    exact: true,
    name: 'CompanyVisionBoardUsers',
    component: withCompanySelfDevelopmentLayout(CVisionBoardUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/vision-board/user',
    exact: true,
    name: 'CompanyVisionBoardUserDetail',
    component: withCompanySelfDevelopmentLayout(CVisionBoardUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/users',
    exact: true,
    name: 'CompanySelfDevelopmentUsers',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/self-development/user',
    exact: true,
    name: 'CompanySelfDevelopmentUserDetail',
    component: withCompanySelfDevelopmentLayout(CSelfDevelopmentUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // ---- Scans: Skin, Teeth and Age. Take-up and whether the analysis works;
  // no page in this section can show what a scan found.
  {
    path: '/:slug/scans',
    exact: true,
    name: 'CompanyScansOverview',
    component: withCompanyScansLayout(CScansOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // One component serves all three scan pages — it reads the scan from the last
  // path segment, which is also the key the backend expects.
  {
    path: '/:slug/scans/skin',
    exact: true,
    name: 'CompanySkinScan',
    component: withCompanyScansLayout(CScanPage),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/scans/teeth',
    exact: true,
    name: 'CompanyTeethScan',
    component: withCompanyScansLayout(CScanPage),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/scans/age',
    exact: true,
    name: 'CompanyAgeScan',
    component: withCompanyScansLayout(CScanPage),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/scans/users',
    exact: true,
    name: 'CompanyScanUsers',
    component: withCompanyScansLayout(CScanUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/scans/user',
    exact: true,
    name: 'CompanyScanUserDetail',
    component: withCompanyScansLayout(CScanUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // ---- Quit Programmes: Last Puff and Last Drink. Three routes, not five:
  // there is deliberately no users list and no per-person page here, because
  // being enrolled is itself health information about an identifiable person.
  {
    path: '/:slug/cessation',
    exact: true,
    name: 'CompanyCessationOverview',
    component: withCompanyCessationLayout(CCessationOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // One component serves both programme pages — it reads the programme from the
  // last path segment.
  {
    path: '/:slug/cessation/last-puff',
    exact: true,
    name: 'CompanyLastPuff',
    component: withCompanyCessationLayout(CProgrammePage),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/cessation/last-drink',
    exact: true,
    name: 'CompanyLastDrink',
    component: withCompanyCessationLayout(CProgrammePage),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Company Trigger Trace — what set people off, how hard it landed and whether
  // they got past it. The employees tab is engagement only and has no detail
  // route behind it: a trigger log is somebody's private account of a bad
  // moment, and nothing they wrote or felt is ever shown beside their name.
  {
    path: '/:slug/trigger-trace',
    exact: true,
    name: 'CompanyTriggerOverview',
    component: withCompanyTriggerLayout(CTriggerOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/trigger-trace/patterns',
    exact: true,
    name: 'CompanyTriggerPatterns',
    component: withCompanyTriggerLayout(CTriggerPatterns),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/trigger-trace/resolution',
    exact: true,
    name: 'CompanyTriggerResolution',
    component: withCompanyTriggerLayout(CTriggerResolution),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/trigger-trace/users',
    exact: true,
    name: 'CompanyTriggerUsers',
    component: withCompanyTriggerLayout(CTriggerUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games',
    exact: true,
    name: 'CompanyGamesOverview',
    component: withCompanyGamesLayout(CGamesOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games/chess',
    exact: true,
    name: 'CompanyGamesChess',
    component: withCompanyGamesLayout(CChess),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games/word-search',
    exact: true,
    name: 'CompanyGamesWordSearch',
    component: withCompanyGamesLayout(CWordSearch),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games/block-fill',
    exact: true,
    name: 'CompanyGamesBlockFill',
    component: withCompanyGamesLayout(CBlockFill),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games/quiz',
    exact: true,
    name: 'CompanyGamesQuiz',
    component: withCompanyGamesLayout(CQuiz),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/games/users',
    exact: true,
    name: 'CompanyGamesUsers',
    component: withCompanyGamesLayout(CGamesUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Guided Path. One kind of plan per tab; the kind is fixed by the route
  // rather than read out of the URL, so a hand-typed segment cannot select a
  // third thing. The users route is engagement only and has nothing behind it.
  {
    path: '/:slug/guided-path',
    exact: true,
    name: 'CompanyGuidedPathOverview',
    component: withCompanyGuidedPathLayout(CGuidedPathOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/guided-path/ai-plan',
    exact: true,
    name: 'CompanyGuidedPathAiPlan',
    component: withCompanyGuidedPathLayout(CGuidedPathAiPlan),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/guided-path/custom-plan',
    exact: true,
    name: 'CompanyGuidedPathCustomPlan',
    component: withCompanyGuidedPathLayout(CGuidedPathCustomPlan),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/guided-path/users',
    exact: true,
    name: 'CompanyGuidedPathUsers',
    component: withCompanyGuidedPathLayout(CGuidedPathUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Care Programmes. NO users route in this section or the next one — being
  // listed would disclose that a named employee is on GLP-1 medication, in
  // fertility treatment, or living with a chronic pain condition.
  {
    path: '/:slug/care',
    exact: true,
    name: 'CompanyCareOverview',
    component: withCompanyCareLayout(CCareOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/care/glp-1',
    exact: true,
    name: 'CompanyCareGlp1',
    component: withCompanyCareLayout(CCareProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/care/pause',
    exact: true,
    name: 'CompanyCarePause',
    component: withCompanyCareLayout(CCareProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/care/peptide',
    exact: true,
    name: 'CompanyCarePeptide',
    component: withCompanyCareLayout(CCareProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/care/fibromyalgia',
    exact: true,
    name: 'CompanyCareFibromyalgia',
    component: withCompanyCareLayout(CCareProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Reproductive Health, deliberately its own section rather than two more
  // tabs above — see the layout's header.
  {
    path: '/:slug/reproductive-health',
    exact: true,
    name: 'CompanyReproductiveOverview',
    component: withCompanyReproductiveLayout(CReproductiveOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/reproductive-health/menstrual',
    exact: true,
    name: 'CompanyReproductiveMenstrual',
    component: withCompanyReproductiveLayout(CReproductiveProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/reproductive-health/fertility',
    exact: true,
    name: 'CompanyReproductiveFertility',
    component: withCompanyReproductiveLayout(CReproductiveProgramme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // The old standalone Vision Board URLs. Kept as redirects so bookmarks and
  // pasted links still land somewhere, rather than 404ing.
  {
    path: '/:slug/vision-board',
    exact: true,
    name: 'CompanyVisionBoardLegacy',
    component: VisionBoardDashboardRedirect,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/vision-board/users',
    exact: true,
    name: 'CompanyVisionBoardUsersLegacy',
    component: VisionBoardUsersRedirect,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/vision-board/user',
    exact: true,
    name: 'CompanyVisionBoardUserLegacy',
    component: VisionBoardUserRedirect,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/moneymind',
    exact: true,
    name: 'CompanyMoneyMindDashboard',
    component: withCompanyMoneyMindLayout(CMoneyMindDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/moneymind/users',
    exact: true,
    name: 'CompanyMoneyMindUsers',
    component: withCompanyMoneyMindLayout(CMoneyMindUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/moneymind/user',
    exact: true,
    name: 'CompanyMoneyMindUserDetail',
    component: withCompanyMoneyMindLayout(CMoneyMindUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/elite-mind',
    exact: true,
    name: 'CompanyEliteMindDashboard',
    component: withCompanyEliteMindLayout(CEliteMindDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/elite-mind/users',
    exact: true,
    name: 'CompanyEliteMindUsers',
    component: withCompanyEliteMindLayout(CEliteMindUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/elite-mind/user',
    exact: true,
    name: 'CompanyEliteMindUserDetail',
    component: withCompanyEliteMindLayout(CEliteMindUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/sleep',
    exact: true,
    name: 'CompanySleepTracker',
    component: withCompanySleepLayout(CSleepTracker),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/sleep/sounds',
    exact: true,
    name: 'CompanySleepSounds',
    component: withCompanySleepLayout(CSleepSounds),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/sleep/users',
    exact: true,
    name: 'CompanySleepUsers',
    component: withCompanySleepLayout(CSleepUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/sleep/user',
    exact: true,
    name: 'CompanySleepUserDetail',
    component: withCompanySleepLayout(CSleepUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/habits',
    exact: true,
    name: 'CompanyHabitsTracker',
    component: withCompanyHabitsLayout(CHabitsTracker),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/habits/users',
    exact: true,
    name: 'CompanyHabitsUsers',
    component: withCompanyHabitsLayout(CHabitsUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/habits/user',
    exact: true,
    name: 'CompanyHabitsUserDetail',
    component: withCompanyHabitsLayout(CHabitsUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Challenges insights. Analytics only — /:slug/challenges (below) keeps the
  // create/edit/rewards flow the sidebar points at.
  {
    path: '/:slug/challenges/insights',
    exact: true,
    name: 'CompanyChallengesOverview',
    component: withCompanyChallengesLayout(CChallengesDashboard),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges/insights/challenges',
    exact: true,
    name: 'CompanyChallengesList',
    component: withCompanyChallengesLayout(CChallengesList),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges/insights/users',
    exact: true,
    name: 'CompanyChallengesUsers',
    component: withCompanyChallengesLayout(CChallengesUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges/insights/user',
    exact: true,
    name: 'CompanyChallengesUserDetail',
    component: withCompanyChallengesLayout(CChallengesUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/myCompany',
    exact: true,
    name: 'MyCompany',
    component: MyCompany,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/child-companies',
    exact: true,
    name: 'ChildCompanies',
    component: ChildCompanies,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/push-notification',
    exact: true,
    name: 'CompanyNotification',
    component: Notification,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },

  {
    path: '/:slug/push-notification/add',
    exact: true,
    name: 'BTBAddPushNotification',
    component: BTBAddNotification,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/health-videos',
    exact: true,
    name: 'healthVideos',
    component: HealthVideos,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/health-videos/all',
    exact: true,
    name: 'allHealthVideos',
    component: AllHealthVideos,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/health-videos/view-health-video',
    exact: true,
    name: 'viewHealthVideo',
    component: ViewHealthVideo,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/report',
    exact: true,
    name: 'CompanyReport',
    component: CompanyReport,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/badges',
    exact: true,
    name: 'CompanyReport',
    // component: BadgeDashboard,
    component: CBadges,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/integrations',
    exact: true,
    name: 'Integrations',
    component: Integrations,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/integrations',
    exact: true,
    name: 'Integrations',
    component: Integrations,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/solution',
    exact: true,
    name: 'CompanySolution',
    component: CompanySolution,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/team-challanges',
    exact: true,
    name: 'TeamChallanges',
    // component: ChallangesDashboard,
    component: UnderMaintenance,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/team-challanges/view-all-challenges',
    exact: true,
    name: 'TeamChallanges',
    component: ViewAllChallanges,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/team-challanges/view-all-rewards',
    exact: true,
    name: 'TeamChallanges',
    component: ViewAllRewards,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/team-challanges/add-edit-challenges',
    exact: true,
    name: 'TeamChallanges',
    component: AddEditChallanges,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/team-challanges/add-edit-weekly-challenges',
    exact: true,
    name: 'TeamChallanges',
    component: AddEditWeeklyChallanges,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/team-challanges/add-edit-rewards',
    exact: true,
    name: 'TeamChallanges',
    component: AddEditRewards,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: true
  },
  {
    path: '/:slug/users',
    exact: true,
    name: 'CompanyUser',
    component: CompanyUser,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/adduser',
    exact: true,
    name: 'AddUser',
    component: AddUser,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/users/edituser',
    exact: true,
    name: 'AddUser',
    component: AddUser,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/notifications',
    exact: true,
    name: 'Notification',
    // component: Notification,
    component: ViewNotification,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },

  {
    path: '/:slug/notifications/view',
    exact: true,
    name: 'Notification',
    component: ViewNotification,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/change-password',
    exact: true,
    name: 'ChangePassword',
    component: ChangePassword,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },

  {
    path: '/:slug/sub-admins',
    exact: true,
    name: 'SubAdmin',
    component: SubAdmin,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/sub-admins/add-edit',
    exact: true,
    name: 'AddEditSubAdmin',
    component: AddEditSubAdmin,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },

  {
    path: '/:slug/users/view',
    exact: true,
    name: 'ViewEditUser',
    component: ViewEditUser,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // {
  //   path: '/users/view',
  //   exact: true,
  //   name: 'ViewEditUser',
  //   component: ViewEditUser,
  //   private: true,
  //   onlyCompanyAdmin: true,
  //   onlyAdmin: false
  // },
  // Survey Routes Starts
  {
    path: '/:slug/surveys/my-survey',
    exact: false,
    name: 'MySurvey',
    component: MySurvey,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/surveys',
    exact: true,
    name: 'Survey Dashboard',
    component: SurveyDashboard,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/surveys/summary/:surveyId',
    exact: false,
    name: 'Survey Summary',
    component: SurveyStatistics,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/surveys/add-edit',
    exact: true,
    name: 'Survey Add Edit',
    component: AddEditSurvey,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/surveys/preview',
    exact: false,
    name: 'Survey Preview',
    component: PreviewSurvey,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/b2b-users',
    exact: true,
    name: 'Badges & Wellpoints',
    component: UnderMaintenance,
    // component: B2cUsers,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges',
    exact: true,
    name: 'Challenges',
    // component: UnderMaintenance,
    component: Challenges,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges/create-new-challenges',
    exact: true,
    name: 'Challenges',
    component: NewChallenges,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/challenges/view-challenges',
    exact: true,
    name: 'Challenges',
    component: ViewChallenges,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Offerings page for B2B Admins
  {
    path: '/:slug/offering',
    exact: true,
    name: 'Offerings',
    component: B2BOfferings,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/offering/create',
    exact: true,
    name: 'Create Custom Offering',
    component: B2BCreateCustomOffering,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/offering/manage/:id',

    name: 'Manage Custom Offering',
    component: ManageOffering,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  // Marketplace insights dashboards (must come before general marketplace route)
  {
    path: '/:slug/marketplace/insights',
    exact: true,
    name: 'CompanyMarketplaceEGifts',
    component: withCompanyMarketplaceLayout(CMarketplaceEGifts),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/marketplace/insights/vip',
    exact: true,
    name: 'CompanyMarketplaceVip',
    component: withCompanyMarketplaceLayout(CMarketplaceVip),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/marketplace/insights/users',
    exact: true,
    name: 'CompanyMarketplaceUsers',
    component: withCompanyMarketplaceLayout(CMarketplaceUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/marketplace/insights/user',
    exact: true,
    name: 'CompanyMarketplaceUserDetail',
    component: withCompanyMarketplaceLayout(CMarketplaceUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Perks section (Instant Support + Salary Sacrifice + Health Scheme +
  // Custom Perks). The layout hides the tabs a company doesn't have and
  // redirects one that has none; the endpoints enforce the same rule.
  {
    path: '/:slug/perks',
    exact: true,
    name: 'CompanyPerksOverview',
    component: withCompanyPerksLayout(CPerksOverview),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/instant-support',
    exact: true,
    name: 'CompanyPerksInstantSupport',
    component: withCompanyPerksLayout(CPerksInstantSupport),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/salary-sacrifice',
    exact: true,
    name: 'CompanyPerksSalarySacrifice',
    component: withCompanyPerksLayout(CPerksSalarySacrifice),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/health-scheme',
    exact: true,
    name: 'CompanyPerksHealthScheme',
    component: withCompanyPerksLayout(CPerksHealthScheme),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/custom',
    exact: true,
    name: 'CompanyPerksCustom',
    component: withCompanyPerksLayout(CPerksCustom),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/users',
    exact: true,
    name: 'CompanyPerksUsers',
    component: withCompanyPerksLayout(CPerksUsers),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/perks/user',
    exact: true,
    name: 'CompanyPerksUserDetail',
    component: withCompanyPerksLayout(CPerksUserDetail),
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // Marketplace Cart page (must come before general marketplace route)
  {
    path: '/:slug/marketplace/cart',
    exact: true,
    name: 'Cart',
    component: Cart,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Marketplace Orders page (must come before general marketplace route)
  {
    path: '/:slug/marketplace/orders',
    exact: true,
    name: 'Orders',
    component: Orders,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Marketplace Product Detail page (must come before general marketplace route)
  {
    path: '/:slug/marketplace/product/:productId',
    exact: true,
    name: 'Product Detail',
    component: ProductDetail,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Marketplace page for B2B Admins
  {
    path: '/:slug/marketplace',
    exact: true,
    name: 'Marketplace',
    component: Marketplace,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 1
  {
    path: '/:slug/instant-support',
    exact: true,
    name: 'Instant Support (EAP)',
    component: InstantSupportDashboard,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  //Benefits main Screen 1
  {
    path: '/:slug/benefits',
    exact: true,
    name: 'Shoorah Benefits',
    component: UnderMaintenance,
    // component: BenefitsDashboard,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Benefits View Benefits screen 2
  {
    path: '/:slug/benefits/view-benefits',
    exact: true,
    name: 'View Benefits',
    component: ViewBenefitsPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Benefits Add Benefits screen 2
  {
    path: '/:slug/benefits/add-custom-benefits',
    exact: true,
    name: 'Add Custom Benefits',
    component: AddCustomBenefitsPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 2
  {
    path: '/:slug/instant-support/get-support',
    exact: true,
    name: 'Get Support',
    component: GetSupport,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 3
  {
    path: '/:slug/instant-support/checkout',
    exact: true,
    name: 'Instant Support Checkout',
    component: InstantSupportCheckoutPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 4
  {
    path: '/:slug/instant-support/upgrade-plans',
    exact: true,
    name: 'Instant Support Checkout',
    component: UpgradeUsersForInstantSupportPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 5
  {
    path: '/:slug/instant-support/add-custom-number',
    exact: true,
    name: 'Add Custom Number',
    component: InstantSupportAddCustomNumber,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },

  // Salary Sacrifice Routes
  {
    path: '/:slug/salary-sacrifice',
    exact: true,
    name: 'Salary Sacrifice',
    component: SalarySacrifice,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/salary-sacrifice/schemes',
    exact: true,
    name: 'Salary Sacrifice Schemes',
    component: AddEditScheme,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // Health Scheme Routes
  {
    path: '/:slug/health-scheme',
    exact: true,
    name: 'Health Scheme',
    component: HealthSchemePage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/health-scheme/scheme',
    exact: true,
    name: 'Add/Edit Health Scheme',
    component: AddEditHealthScheme,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/health-scheme/view',
    exact: true,
    name: 'View Health Scheme',
    component: AdminHealthSchemes,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },

  // test feature B2b Side index.js page route
  {
    path: '/:slug/test',
    exact: true,
    name: 'Test Feature',
    component: UnderMaintenance,
    // component: TestLandingPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/test/create-test',
    exact: true,
    name: 'Create Tests',
    component: UnderMaintenance,
    //component: CreateTestPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 4
  {
    path: '/:slug/instant-support/upgrade-plans',
    exact: true,
    name: 'Instant Support Checkout',
    component: UpgradeUsersForInstantSupportPage,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  // instant support screen 5
  {
    path: '/:slug/instant-support/add-custom-number',
    exact: true,
    name: 'Add Custom Number',
    component: InstantSupportAddCustomNumber,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },

  {
    path: '/:slug/pulse-survey',
    exact: true,
    name: 'pulse survey',
    // component: UnderMaintenance,
    component: PulseSurveyDashboard,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/pulse-survey/my-survey',
    exact: true,
    name: 'pulse survey',
    component: MyPulseSurvey,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/pulse-survey/add-edit-survey',
    exact: true,
    name: 'addEditSurvey',
    component: AddEditPulseSurvey,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/preview/pulse-surveys',
    exact: false,
    name: 'Pulse Survey Preview',
    component: PreviewPulseSurveys,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/:slug/survey-list',
    exact: false,
    name: 'MySurveyList',
    component: PulseSurvey,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  // Peap Menu
  {
    path: '/:slug/peap-dashboard',
    exact: true,
    name: 'Peap Dashboard',
    // component: B2BPeapDashboard,
    component: UnderMaintenance,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/:slug/peap-wellbank',
    exact: true,
    name: 'Peap WellBank',
    //component:UnderMaintenance,
    component: WellBankDashboard,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/:slug/peap-wellbank/manage-wellpoint',
    exact: true,
    name: 'Manage WellPoints',
    component: ManageWellPoint,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/manage-request',
    exact: true,
    name: 'Manage Requests',
    component: ManageRequest,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/purchase-history/:id',
    exact: true,
    name: 'View Previous Invoice',
    component: WellbankInvoiceDetails,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/purchase-wellpoint',
    exact: true,
    name: 'Purchase WellPoints',
    component: PurchaseWellPoint,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/buy-now',
    exact: true,
    name: 'Buy Now Well Points',
    component: BuyNowWellPoint,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/buy-now/summary',
    exact: true,
    name: 'Buy Now Purchase Summary',
    component: BuyNowSummary,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/order-now',
    exact: true,
    name: 'Order Now Well Points',
    component: OrderNowWellPoint,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/order-now/summary',
    exact: true,
    name: 'Order Now Purchase Summary',
    component: OrderNowSummary,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/purchase-history',
    exact: true,
    name: 'Previous Purchase’s',
    component: PurchaseHistory,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/transaction-history',
    exact: true,
    name: 'Transaction History',
    component: TransactionHistory,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/individual-distribution',
    exact: true,
    name: 'Individual Distribution',
    component: IndividualDistribution,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/individual-distribution/individual-amount',
    exact: true,
    name: 'Individual Amount',
    component: IndividualAmount,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/individual-distribution/equal-amount',
    exact: true,
    name: 'Equal Amount',
    component: EqualAmount,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/team-distribution',
    exact: true,
    name: 'Team Distribution',
    component: TeamDistribution,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/team-distribution/individual-amount',
    exact: true,
    name: 'Individual Amount',
    component: TeamIndividualAmount,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/team-distribution/equal-amount',
    exact: true,
    name: 'Equal Amount',
    component: TeamEqualAmount,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/company-distribution',
    exact: true,
    name: 'Company Wide Distribution',
    component: CompanyDistribution,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/peap-wellbank/auto-withdraw-distribution',
    exact: true,
    name: 'Auto Withdraw Distribution',
    component: AutoWithdrawDistribution,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: false
  },
  {
    path: '/:slug/content-approval',
    exact: true,
    name: 'ContentApproval',
    component: ContentApproval,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/:slug/content-approval/view',
    exact: true,
    name: 'view-content-approval',
    component: ViewContentApproval,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/:slug/view-survey/:surveyId',
    exact: true,
    name: 'view-view-survey',
    component: ViewSurveyApproval,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  // Survey Routes Ends

  {
    path: '/game/*',
    name: 'EliteGames',
    component: EliteGames,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true,
    children: eliteGameRoutes
  },

  {
    path: '/:slug/kids-list',
    exact: true,
    name: 'Kids',
    component: Kids,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/kids-list',
    exact: true,
    name: 'Kids',
    component: Kids,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/kids/view/:id',
    exact: true,
    name: 'Kids',
    component: KidsView,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true
  },
  {
    path: '/peptides/*',
    name: 'peptide',
    component: Peptides,
    private: true,
    onlyCompanyAdmin: false,
    onlyAdmin: true,
    children: peptideRoutes
  },
  // Shoutouts Routes
  {
    path: '/:slug/shoutouts',
    exact: true,
    name: 'Shoutouts',
    component: Shoutouts,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/shoutouts/send',
    exact: true,
    name: 'SendShoutout',
    component: SendShoutout,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  // People Plan Routes
  {
    path: '/:slug/people-plan',
    exact: true,
    name: 'PeoplePlanDashboard',
    component: PeoplePlanDashboard,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/goals',
    exact: true,
    name: 'PeoplePlanGoals',
    component: PeoplePlanGoals,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/goals/add',
    exact: true,
    name: 'PeoplePlanAddGoal',
    component: AddEditGoal,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/goals/edit/:id',
    exact: true,
    name: 'PeoplePlanEditGoal',
    component: AddEditGoal,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/goals/view/:id',
    exact: true,
    name: 'PeoplePlanViewGoal',
    component: ViewGoal,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/milestones',
    exact: true,
    name: 'PeoplePlanMilestones',
    component: PeoplePlanGoals,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/milestones/add',
    exact: true,
    name: 'PeoplePlanAddMilestone',
    component: AddEditMilestone,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/milestones/edit/:id',
    exact: true,
    name: 'PeoplePlanEditMilestone',
    component: AddEditMilestone,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/milestones/view/:id',
    exact: true,
    name: 'PeoplePlanViewMilestone',
    component: ViewMilestone,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/document-hub',
    exact: true,
    name: 'PeoplePlanDocumentHub',
    component: PeoplePlanDocumentHub,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/document-hub/:folderId',
    exact: true,
    name: 'PeoplePlanDocumentFolderDetail',
    component: PeoplePlanDocumentFolderDetail,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/roadmaps',
    exact: true,
    name: 'PeoplePlanRoadMaps',
    component: PeoplePlanRoadMaps,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/roadmaps/add',
    exact: true,
    name: 'PeoplePlanAddRoadMap',
    component: AddEditRoadMap,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/roadmaps/edit/:id',
    exact: true,
    name: 'PeoplePlanEditRoadMap',
    component: AddEditRoadMap,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/roadmaps/view/:id',
    exact: true,
    name: 'PeoplePlanViewRoadMap',
    component: ViewRoadMap,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/roi-report',
    exact: true,
    name: 'PeoplePlanRoiReport',
    component: PeoplePlanRoiReport,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '/:slug/people-plan/key-dates',
    exact: true,
    name: 'PeoplePlanKeyDates',
    component: PeoplePlanKeyDates,
    private: true,
    onlyCompanyAdmin: true,
    onlyAdmin: false
  },
  {
    path: '*',
    exact: true,
    name: 'NotFound',
    component: NotFound,
    private: false
  }
];
export default routes;
